import { useEffect, useState } from "react";
import { Button, Modal, Space } from "antd";
import { audioMessage as message } from "@components/Common/Notifications/audioMessage";
import { getUserName } from "@utils/rtp_utils";
import { coachName, rtpApi, runRtpWrite, useCoachList, useCurrentUserUuid } from "./rtpHooks";
import { StaffFlagsEditor } from "./StaffFlagsEditor";
import type { RtpPlanDetail, RtpStaffFlags } from "./types";

interface StaffModalProps {
	plan: RtpPlanDetail;
	open: boolean;
	onClose: () => void;
	refetch: () => void;
}

export const StaffModal = ({ plan, open, onClose, refetch }: StaffModalProps) => {
	const coachList = useCoachList();
	const currentUserUuid = useCurrentUserUuid();
	const [staff, setStaff] = useState<Record<string, RtpStaffFlags>>({});
	const [submitting, setSubmitting] = useState(false);
	// False until the open effect copies the saved staff. Before that, `staff` is empty or stale,
	// and every saved manager would flash as "removed" for one frame.
	const [seeded, setSeeded] = useState(false);

	useEffect(() => {
		if (open) {
			const clone: Record<string, RtpStaffFlags> = {};
			for (const [uuid, flags] of Object.entries(plan.relevantStaff)) {
				clone[uuid] = flags.CanManagePlan
					? { CanManagePlan: true, CanCreateDayPlan: true, CanCreateExitCriteria: true }
					: { ...flags };
			}
			setStaff(clone);
		}
		setSeeded(open);
		// Re-clone only when the modal opens, not on every plan refetch while it's already open
		// (that would clobber in-progress edits).
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open]);

	const getName = (uuid: string): string => {
		const coach = coachList.find((c) => c.uuid === uuid);
		return coach ? coachName(coach) : getUserName(plan, uuid);
	};

	// Plan Managers removed from the list have no row to show a note in, so they are listed above
	// the footer. Removing your own Plan Manager access (unchecked or removed) turns Save red.
	const isManager = (flags?: RtpStaffFlags) => Boolean(flags?.CanManagePlan);
	const removedManagers = seeded
		? Object.keys(plan.relevantStaff).filter((uuid) => isManager(plan.relevantStaff[uuid]) && !(uuid in staff))
		: [];
	const removesOwnAccess =
		seeded && isManager(plan.relevantStaff[currentUserUuid]) && !isManager(staff[currentUserUuid]);

	const handleSave = async () => {
		if (!Object.values(staff).some((flags) => flags.CanManagePlan)) {
			message.error("At least one staff member must manage the plan.");
			return;
		}
		setSubmitting(true);
		await runRtpWrite(
			() => rtpApi.patchRtpPlan(plan.id, { relevantStaff: staff }),
			() => {
				refetch();
				onClose();
			}
		);
		setSubmitting(false);
	};

	return (
		<Modal
			className="rtp-wz rtp-staff-modal"
			title={
				<div>
					<div>Manage Staff</div>
					<div className="rtp-staff-modal-sub">Choose who works on this plan and what each person can do.</div>
				</div>
			}
			open={open}
			onCancel={onClose}
			width={620}
			destroyOnHidden
			footer={
				<Space>
					<Button data-attr="settings.rtp-plan.staff-cancel" onClick={onClose} disabled={submitting}>
						Cancel
					</Button>
					<Button
						type="primary"
						danger={removesOwnAccess}
						loading={submitting}
						data-attr="settings.rtp-plan.staff-submit"
						onClick={handleSave}
					>
						{removesOwnAccess ? "Save and remove my access" : "Save"}
					</Button>
				</Space>
			}
		>
			{seeded ? (
				<StaffFlagsEditor
					staff={staff}
					onChange={setStaff}
					getName={getName}
					savedStaff={plan.relevantStaff}
					removeDataAttr="settings.rtp-plan.staff-remove"
				/>
			) : null}
			{removedManagers.length > 0 ? (
				<div className="rtp-staff-removed">
					{removedManagers.map((uuid) => (
						<div
							key={uuid}
							className={`rtp-staff-note rtp-staff-note--${uuid === currentUserUuid ? "self" : "lose"}`}
						>
							{uuid === currentUserUuid
								? "You will be removed from this plan and lose access when you save."
								: `${getName(uuid)} (Plan Manager) will be removed from this plan when you save.`}
						</div>
					))}
				</div>
			) : null}
		</Modal>
	);
};
